/**
 * 设备扩展参数工具函数
 */

/**
 * 验证扩展参数JSON格式
 * @param {string} jsonString JSON字符串
 * @returns {Object} 验证结果 {valid: boolean, error: string, data: Object}
 */
export function validateExtendParamsJson(jsonString) {
  try {
    const data = JSON.parse(jsonString || '{}')
    return {
      valid: true,
      error: null,
      data
    }
  } catch (error) {
    return {
      valid: false,
      error: error.message,
      data: null
    }
  }
}

/**
 * 合并默认参数和当前参数
 * @param {Object} defaultParams 默认参数对象
 * @param {Object} currentParams 当前参数对象
 * @returns {Object} 合并后的参数对象
 */
export function mergeParams(defaultParams = {}, currentParams = {}) {
  const merged = { ...defaultParams }
  
  Object.keys(currentParams).forEach(key => {
    if (currentParams[key] !== undefined) {
      merged[key] = currentParams[key]
    }
  })
  
  return merged
}

/**
 * 获取参数类型
 * @param {any} value 参数值
 * @returns {string} 参数类型: 'boolean', 'number', 'string', 'object', 'array'
 */
export function getParamType(value) {
  if (value === null || value === undefined) {
    return 'string'
  }
  
  if (Array.isArray(value)) {
    return 'array'
  }
  
  const type = typeof value
  
  if (type === 'object') {
    return 'object'
  }
  
  return type
}

/**
 * 格式化扩展参数用于显示
 * @param {string} extendParams JSON字符串
 * @returns {string} 格式化后的显示文本
 */
export function formatExtendParamsForDisplay(extendParams) {
  if (!extendParams) {
    return '无配置'
  }
  
  try {
    const params = JSON.parse(extendParams)
    const keys = Object.keys(params)
    
    if (keys.length === 0) {
      return '空配置'
    }
    
    if (keys.length <= 3) {
      return keys.map(key => `${key}: ${params[key]}`).join(', ')
    } else {
      return `${keys.slice(0, 3).map(key => `${key}: ${params[key]}`).join(', ')}... (共${keys.length}项)`
    }
  } catch (error) {
    return '配置格式错误'
  }
}

/**
 * 检查扩展参数是否为空
 * @param {string} extendParams JSON字符串
 * @returns {boolean} 是否为空
 */
export function isExtendParamsEmpty(extendParams) {
  if (!extendParams) {
    return true
  }
  
  try {
    const params = JSON.parse(extendParams)
    return Object.keys(params).length === 0
  } catch (error) {
    return true
  }
}

/**
 * 生成参数默认值
 * @param {string} type 参数类型
 * @param {string} key 参数键名
 * @returns {any} 默认值
 */
export function generateDefaultValue(type, key = '') {
  switch (type) {
    case 'boolean':
      return false
    case 'number':
      if (key.toLowerCase().includes('port')) {
        return 8080
      }
      if (key.toLowerCase().includes('timeout')) {
        return 30
      }
      if (key.toLowerCase().includes('interval')) {
        return 5
      }
      return 0
    case 'array':
      return []
    case 'object':
      return {}
    default:
      return ''
  }
}

export default {
  validateExtendParamsJson,
  mergeParams,
  getParamType,
  formatExtendParamsForDisplay,
  isExtendParamsEmpty,
  generateDefaultValue
}
